import RAPIER from '@dimforge/rapier3d-compat';
import type { Vec3 } from '../types';
import { BALL_RADIUS, BALL_MASS, GRAVITY, PHYSICS_DT } from '../constants';
import type { SurfaceProperties } from '../types';
import { turfRestitution } from './TurfModel';

let rapier: typeof RAPIER;
let world: RAPIER.World;
let groundCollider: RAPIER.Collider;
const ballMaterials = new Map<RAPIER.RigidBody, SurfaceProperties>();

// Frame-idempotent stepping: prevents multiple GroundPhysics instances
// from advancing the world more than once per physics tick.
let frameCounter = 0;
let lastSteppedFrame = -1;

export async function initRapier(): Promise<void> {
  await RAPIER.init();
  rapier = RAPIER;
  world?.free();
  ballMaterials.clear();
  world = new RAPIER.World({ x: 0, y: -GRAVITY, z: 0 });
  frameCounter = 0;
  lastSteppedFrame = -1;
  // Match Rapier integration timestep to our physics loop (1/240s).
  // Rapier defaults to 1/60s, causing 4x time distortion in rollout.
  world.integrationParameters.dt = PHYSICS_DT;
  // Contact tolerances should match a golf ball, not a metre-sized rigid body.
  world.integrationParameters.lengthUnit = 2 * BALL_RADIUS;
  world.integrationParameters.numSolverIterations = 8;

  // Infinite flat range. Neutral terrain multiplies each ball's own material;
  // comparison balls collide with terrain, never with one another.
  const groundBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
  groundCollider = world.createCollider(
    new RAPIER.ColliderDesc(new RAPIER.HalfSpace({ x: 0, y: 1, z: 0 }))
      .setRestitution(1)
      .setFriction(1)
      .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Multiply)
      .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Multiply)
      .setCollisionGroups(0x00010002),
    groundBody,
  );
}

export function getRapier(): typeof RAPIER {
  return rapier;
}

export function getWorld(): RAPIER.World {
  return world;
}

export function getGroundCollider(): RAPIER.Collider {
  return groundCollider;
}

export function createBallBody(
  position: Vec3,
  velocity: Vec3,
  surfaceProps: SurfaceProperties,
  angularVelocity: Vec3 = { x: 0, y: 0, z: 0 },
): RAPIER.RigidBody {
  const bodyDesc = rapier.RigidBodyDesc.dynamic()
    .setTranslation(position.x, position.y, position.z)
    .setLinvel(velocity.x, velocity.y, velocity.z)
    .setAngvel(angularVelocity)
    .setCcdEnabled(true);

  const body = world.createRigidBody(bodyDesc);

  const volume = (4 / 3) * Math.PI * Math.pow(BALL_RADIUS, 3);
  const colliderDesc = rapier.ColliderDesc.ball(BALL_RADIUS)
    .setRestitution(surfaceProps.restitution)
    .setFriction(surfaceProps.friction)
    .setRestitutionCombineRule(rapier.CoefficientCombineRule.Multiply)
    .setFrictionCombineRule(rapier.CoefficientCombineRule.Multiply)
    .setCollisionGroups(0x00020001)
    .setDensity(BALL_MASS / volume);

  world.createCollider(colliderDesc, body);
  ballMaterials.set(body, { ...surfaceProps });

  return body;
}

/** Call once per physics tick, before any GroundPhysics.step() calls. */
export function markNewFrame(): void {
  frameCounter++;
}

/**
 * Step the Rapier world. Frame-idempotent: multiple calls in the same
 * frame (from multiple GroundPhysics instances) only step once.
 */
export function stepWorld(): void {
  if (lastSteppedFrame === frameCounter) return;
  lastSteppedFrame = frameCounter;
  // Update all balls before a shared step, independent of caller/update order.
  for (const [body, material] of ballMaterials) {
    body.collider(0).setRestitution(turfRestitution(Math.max(0, -body.linvel().y), material.restitution));
  }
  world.step();
}

export function removeBallBody(body: RAPIER.RigidBody): void {
  ballMaterials.delete(body);
  world.removeRigidBody(body);
}
